import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AppSelect from './AppSelect';
import { normalizeGenre, normalizeKey, normalizeType } from '../utils/normalize';
import { buildInternalLinkHtml } from '../utils/internalNoteLinks';
import CustomTagInput from './CustomTagInput';
import dexie from '../offlineDB';
import { addPendingWrite } from '../utils/flushPendingWrites';
import { parseBulkTuneText, parseSpreadsheetTunes } from '../utils/bulkTuneParsers';
import { parseBulkNoteText } from '../utils/bulkNoteParsers';
import { parseBulkUrtextText } from '../utils/urtextImportParser';
import { cleanAndFillAbc } from '../utils/cleanAndFillAbc';

const BULK_IMPORT_FILE_ACCEPT = '.txt,.md,.markdown,.cho,.chopro,.pro,.urtext,.csv,.tsv,.xlsx,.xls';
const formatUntitledDate = () => new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
const URTEXT_CHILDREN_START = '<!-- urtext-children:start -->';
const URTEXT_CHILDREN_END = '<!-- urtext-children:end -->';

const buildCombinedLyrics = (song = {}) => {
  const chords = String(song.chords || '').trim();
  const lyrics = String(song.lyrics || '').trim();
  if (chords && lyrics) return `${chords}\n\n${lyrics}`;
  return chords || lyrics || '';
};

const parseByMode = (mode, value, parserMode = 'standard') => {
  if (mode === 'notes') {
    if (parserMode === 'urtext') return parseBulkUrtextText(value);
    return parseBulkNoteText(value);
  }
  if (parserMode === 'spreadsheet') return parseSpreadsheetTunes(value);
  return parseBulkTuneText(value, 'auto');
};

const baseSetShape = (title, tunes, mode) => {
  const nowIso = new Date().toISOString();
  return {
    title,
    notes: '',
    rating: '',
    ratingLabel: '',
    learnedFrom: '',
    tunes,
    mode,
    customTags: [],
    createdAt: nowIso,
    updatedAt: nowIso
  };
};

const mergeUrtextChildrenIntoNotes = (existingNotes = '', children = []) => {
  const normalized = String(existingNotes || '').trim();
  const links = children
    .map((child) => buildInternalLinkHtml({
      targetType: 'note',
      targetId: child.id,
      title: child.title || 'Untitled Note',
    }))
    .filter(Boolean);
  if (!links.length) return normalized;
  const block = `${URTEXT_CHILDREN_START}<div class="urtext-import-children">${links.join('<br/>')}</div>${URTEXT_CHILDREN_END}`;
  const withoutOld = normalized
    .replace(new RegExp(`${URTEXT_CHILDREN_START}[\\s\\S]*?${URTEXT_CHILDREN_END}`, 'g'), '')
    .trim();
  return [withoutOld, block].filter(Boolean).join('\n\n');
};

export default function BulkTuneImporter({
  user,
  effectiveMode,
  tunes = [],
  genreOptions = [],
  typeOptions = [],
  defaultGenre = '',
  defaultType = '',
  defaultStatus = '',
  onComplete,
}) {
  const isNotesMode = effectiveMode === 'notes';
  const itemLabel = isNotesMode ? 'notes' : 'songs';
  const collectionLabel = isNotesMode ? 'folder' : 'set';
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportGenre, setBulkImportGenre] = useState('');
  const [bulkImportType, setBulkImportType] = useState('');
  const [bulkImportParsed, setBulkImportParsed] = useState([]);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [createMode, setCreateMode] = useState('none'); // 'none' | 'set' | 'session'
  const [bulkImportSetTitle, setBulkImportSetTitle] = useState('');
  const [bulkImportSessionLocation, setBulkImportSessionLocation] = useState('');
  const [bulkImportSessionDate, setBulkImportSessionDate] = useState('');
  const [bulkImportTags, setBulkImportTags] = useState([]);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [noteParserMode, setNoteParserMode] = useState('standard');
  const [tuneParserMode, setTuneParserMode] = useState('standard');
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetFetching, setSheetFetching] = useState(false);
  const [enrichFromTheSession, setEnrichFromTheSession] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  useEffect(() => {
    setBulkImportGenre((prev) => prev || defaultGenre || '');
    setBulkImportType((prev) => prev || defaultType || '');
  }, [defaultGenre, defaultType]);

  const bulkGenreSelectOptions = useMemo(
    () => [{ value: '', label: 'Leave blank' }, ...genreOptions.map((g) => ({ value: g, label: g }))],
    [genreOptions]
  );
  const bulkTypeSelectOptions = useMemo(
    () => [{ value: '', label: 'Leave blank' }, ...typeOptions.map((t) => ({ value: t, label: t }))],
    [typeOptions]
  );

  const handleBulkImportFile = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files.length) return;
    const file = files[0];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext === 'pdf') {
      alert('PDF upload is not supported yet. Copy text from the PDF and paste it below, or use .txt/.md files.');
      if (e?.target) e.target.value = '';
      return;
    }
    try {
      if ((ext === 'xlsx' || ext === 'xls') && !isNotesMode) {
        const xlsxLib = await import('xlsx');
        const wb = xlsxLib.read(await file.arrayBuffer(), { type: 'array' });
        const csv = xlsxLib.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]]);
        setBulkImportText(csv);
        setTuneParserMode('spreadsheet');
        setBulkImportParsed(parseSpreadsheetTunes(csv));
        setShowImportConfirm(false);
      } else if (files.length > 1 && isNotesMode && noteParserMode === 'urtext') {
        const TEXT_EXTS = new Set(['txt', 'md', 'markdown', 'urtext', 'pro', 'cho', 'chopro']);
        const textFiles = files.filter((f) => TEXT_EXTS.has((f.name.split('.').pop() || '').toLowerCase()));
        const filesToRead = textFiles.length > 0 ? textFiles : files;
        const texts = await Promise.all(filesToRead.map((f) => f.text()));
        const allEntries = texts.flatMap((t) => parseBulkUrtextText(t));
        setBulkImportText(texts.join('\n\n'));
        setBulkImportParsed(allEntries);
        setShowImportConfirm(false);
      } else {
        const text = await file.text();
        const nextParserMode = (ext === 'csv' || ext === 'tsv') && !isNotesMode ? 'spreadsheet' : noteParserMode;
        if ((ext === 'csv' || ext === 'tsv') && !isNotesMode) setTuneParserMode('spreadsheet');
        setBulkImportText(text);
        setBulkImportParsed(parseByMode(effectiveMode, text, isNotesMode ? noteParserMode : nextParserMode));
        setShowImportConfirm(false);
      }
    } catch (err) {
      console.error('Failed reading bulk import file:', err);
      alert('Could not read that file.');
    } finally {
      if (e?.target) e.target.value = '';
    }
  };

  const activeParserMode = isNotesMode ? noteParserMode : tuneParserMode;

  const handleFetchSheet = async () => {
    const match = sheetUrl.match(/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    if (!match) { alert('Not a valid Google Sheet URL.'); return; }
    const csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv`;
    setSheetFetching(true);
    try {
      const res = await fetch(csvUrl);
      if (!res.ok) throw new Error('Could not fetch sheet — make sure it is publicly shared (Anyone with link can view).');
      const csv = await res.text();
      setBulkImportText(csv);
      setBulkImportParsed(parseSpreadsheetTunes(csv));
      setShowImportConfirm(false);
      setSheetUrl('');
    } catch (err) {
      alert(err.message || 'Failed to fetch Google Sheet.');
    } finally {
      setSheetFetching(false);
    }
  };

  const fetchTheSessionAbc = async (title, preferredKey = '') => {
    try {
      const sr = await fetch(`https://thesession.org/tunes/search?q=${encodeURIComponent(title)}&format=json`);
      if (!sr.ok) return null;
      const sd = await sr.json();
      const top = (sd.tunes || [])[0];
      if (!top) return null;
      const tr = await fetch(`https://thesession.org/tunes/${top.id}?format=json`);
      if (!tr.ok) return null;
      const td = await tr.json();
      const settings = td.settings || [];
      if (!settings.length) return null;
      let setting = settings[0];
      if (preferredKey) {
        const match = settings.find(s => normalizeKey(s.key || '') === normalizeKey(preferredKey));
        if (match) setting = match;
      }
      const rawAbc = [
        'X:1',
        `T:${td.name || ''}`,
        `M:${setting.meter || ''}`,
        `L:${setting.unit_note_length || ''}`,
        `R:${td.type || ''}`,
        `K:${setting.key || ''}`,
        setting.abc || ''
      ].join('\n');
      return {
        abc: cleanAndFillAbc({ rawAbc, type: td.type, key: setting.key }),
        link: `https://thesession.org/tunes/${top.id}`,
        type: td.type || '',
        key: setting.key || '',
      };
    } catch {
      return null;
    }
  };

  const previewBulkImport = () => {
    const parsed = parseByMode(effectiveMode, bulkImportText, activeParserMode);
    setBulkImportParsed(parsed);
    setShowImportConfirm(false);
    if (!parsed.length) {
      alert(
        isNotesMode
          ? noteParserMode === 'urtext'
            ? 'No Urtext notes detected. Check brace-delimited nodes like { Title _ ... }.'
            : 'No notes detected. Try separators (--- or —), large blank gaps, or title lines ending with _.'
          : tuneParserMode === 'spreadsheet'
            ? 'No rows detected. Make sure the first column has tune titles.'
            : 'No songs detected. Use separators (---) between songs or ChordPro {title: ...} blocks.'
      );
    }
  };

  const requestImportConfirmation = () => {
    const parsed = bulkImportParsed.length ? bulkImportParsed : parseByMode(effectiveMode, bulkImportText, activeParserMode);
    setBulkImportParsed(parsed);
    if (!parsed.length) {
      alert(`No ${itemLabel} to import.`);
      return;
    }
    setShowImportConfirm(true);
  };

  const importBulkEntries = async (entries = null) => {
    if (!user) return;
    const parsed = entries || (bulkImportParsed.length ? bulkImportParsed : parseByMode(effectiveMode, bulkImportText, activeParserMode));
    if (!parsed.length) {
      alert(`No ${itemLabel} to import.`);
      return;
    }

    const normalizedImportType = (() => {
      const raw = normalizeType((bulkImportType || '').trim());
      if (raw) return raw;
      if (isNotesMode) return '';
      return normalizeType(defaultType || '') || 'Song';
    })();
    const normalizedImportGenre = normalizeGenre((bulkImportGenre || '').trim() || defaultGenre || '');

    // Pre-compute session info before the tune loop so we can embed sessionId in each tune
    const importSessionId = createMode === 'session' ? crypto.randomUUID() : null;
    const importSessionLoc = createMode === 'session'
      ? ((bulkImportSessionLocation || '').trim() || 'Imported Session')
      : '';
    const importSessionDateStr = createMode === 'session'
      ? (bulkImportSessionDate ? new Date(bulkImportSessionDate + 'T12:00:00').toISOString() : new Date().toISOString())
      : '';

    setBulkImportLoading(true);
    let importedCount = 0;
    let skippedCount = 0;
    let enrichedCount = 0;
    let createdSetId = '';
    let createdSetTitle = '';
    const createdSetTunes = [];

    try {
      const existingTitleType = isNotesMode
        ? new Set()
        : new Set(
            tunes
              .filter((t) => (t.mode || 'tunes') === effectiveMode)
              .map((t) => `${(t?.title || '').trim().toLowerCase()}::${normalizeType(t?.type || '')}`)
          );

      const preparedEntries = parsed.map((entry) => ({
        ...entry,
        _docId: crypto.randomUUID(),
      }));
      const urtextPathToDocId = new Map();
      preparedEntries.forEach((entry) => {
        const path = String(entry.urtextPath || '').trim();
        if (!path || urtextPathToDocId.has(path)) return;
        urtextPathToDocId.set(path, entry._docId);
      });

      for (const entry of preparedEntries) {
        const resolvedTitle = (entry.title || '').trim() || `${isNotesMode ? 'Untitled Note' : 'Untitled Tune'} - ${formatUntitledDate()}`;
        const entryType = !isNotesMode ? (normalizeType(entry.type || '') || normalizedImportType) : '';
        const entryGenre = !isNotesMode ? (normalizeGenre(entry.genre || '') || normalizedImportGenre) : normalizedImportGenre;
        const entryStatus = !isNotesMode ? ((entry.status || '').trim() || (defaultStatus === '—' ? '' : defaultStatus || '')) : '';
        const duplicateKey = `${resolvedTitle.toLowerCase()}::${entryType}`;
        if (!isNotesMode && existingTitleType.has(duplicateKey)) {
          skippedCount += 1;
          continue;
        }

        const docId = entry._docId;
        const importedOnValue = navigator.onLine ? serverTimestamp() : new Date().toISOString();
        const parsedCreatedAt = isNotesMode ? String(entry.createdAtIso || '').trim() : '';
        const createdAtValue = parsedCreatedAt || (navigator.onLine ? serverTimestamp() : new Date().toISOString());
        const updatedAtValue = parsedCreatedAt || (navigator.onLine ? serverTimestamp() : new Date().toISOString());
        const songText = buildCombinedLyrics(entry);
        const noteText = isNotesMode
          ? String(entry.notes || '').trim()
          : String(entry.quickNote || '').trim();
        const longNotesText = isNotesMode
          ? noteText
          : String(entry.notes || '').trim();

        const entryTags = isNotesMode
          ? Array.from(
            new Set([
              ...(Array.isArray(entry.tags) ? entry.tags : []),
              ...bulkImportTags,
              ...(noteParserMode === 'urtext' ? ['urtextimport'] : []),
            ].map((tag) => String(tag || '').trim()).filter(Boolean))
          )
          : [];

        const data = {
          title: resolvedTitle,
          genre: entryGenre,
          type: entryType,
          key: normalizeKey(entry.key || ''),
          status: isNotesMode ? '' : entryStatus,
          composer: '',
          rating: '',
          lyrics: songText,
          quickNote: isNotesMode ? '' : noteText,
          notes: longNotesText,
          link1: '',
          imageUrl: '',
          abc: '',
          mode: effectiveMode,
          chords: songText,
          link2: '',
          link3: '',
          source: '',
          sessions: importSessionId ? [{ sessionId: importSessionId, location: importSessionLoc, startedAt: importSessionDateStr }] : [],
          recordings: [],
          attachments: [],
          customTags: entryTags,
          eventDate: '',
          importedOn: isNotesMode ? importedOnValue : '',
          createdAt: createdAtValue,
          updatedAt: updatedAtValue,
          lastViewedAt: ''
        };
        if (isNotesMode && noteParserMode === 'urtext') {
          data.urtextPath = String(entry.urtextPath || '').trim();
          data.parentUrtextPath = String(entry.parentUrtextPath || '').trim();
          data.urtextParentId = urtextPathToDocId.get(String(entry.parentUrtextPath || '').trim()) || '';
          data.urtextDepth = Number.isFinite(Number(entry.urtextDepth)) ? Number(entry.urtextDepth) : 0;
          data.urtextOrder = Number.isFinite(Number(entry.urtextOrder)) ? Number(entry.urtextOrder) : 0;
          data.source = 'urtext-import';
        }

        if (enrichFromTheSession && !isNotesMode && (entryGenre || '').toLowerCase() === 'irish' && enrichedCount < 50) {
          const tsData = await fetchTheSessionAbc(resolvedTitle, data.key);
          if (tsData) {
            if (tsData.abc) data.abc = tsData.abc;
            if (tsData.link) data.link1 = tsData.link;
            if (!data.type && tsData.type) data.type = normalizeType(tsData.type);
            if (!data.key && tsData.key) data.key = normalizeKey(tsData.key);
          }
          enrichedCount += 1;
        }

        if (navigator.onLine) {
          await setDoc(doc(db, 'users', user.uid, 'tunes', docId), data);
        } else {
          await dexie.tunes.put({ id: docId, ...data });
          await addPendingWrite({ type: 'add', collection: 'tunes', docId, data });
        }

        createdSetTunes.push({
          id: docId,
          title: resolvedTitle,
          type: entryType || (isNotesMode ? 'Note' : 'Song'),
          key: normalizeKey(entry.key || '')
        });
        if (!isNotesMode) existingTitleType.add(duplicateKey);
        importedCount += 1;
      }

      if (isNotesMode && noteParserMode === 'urtext' && preparedEntries.length > 1) {
        const childMap = new Map();
        preparedEntries.forEach((entry) => {
          const parentPath = String(entry.parentUrtextPath || '').trim();
          if (!parentPath) return;
          const parentId = urtextPathToDocId.get(parentPath);
          if (!parentId) return;
          const children = childMap.get(parentId) || [];
          children.push({
            id: entry._docId,
            title: String(entry.title || '').trim() || 'Untitled Note',
            order: Number.isFinite(Number(entry.urtextOrder)) ? Number(entry.urtextOrder) : 0,
          });
          childMap.set(parentId, children);
        });

        for (const [parentId, children] of childMap.entries()) {
          const sortedChildren = [...children].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
          const parentEntry = preparedEntries.find((entry) => entry._docId === parentId);
          const baseNotes = String(parentEntry?.notes || '').trim();
          const mergedNotes = mergeUrtextChildrenIntoNotes(baseNotes, sortedChildren);
          const nowIso = new Date().toISOString();
          if (navigator.onLine) {
            await setDoc(doc(db, 'users', user.uid, 'tunes', parentId), {
              notes: mergedNotes,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          } else {
            await dexie.tunes.update(parentId, {
              notes: mergedNotes,
              updatedAt: nowIso,
            });
            await addPendingWrite({
              id: `${parentId}__urtextChildren__${Date.now()}`,
              type: 'update',
              collection: 'tunes',
              docId: parentId,
              data: {
                notes: mergedNotes,
                updatedAt: nowIso,
              },
            });
          }
        }
      }

      if (createMode === 'set' && createdSetTunes.length > 0) {
        createdSetTitle = (bulkImportSetTitle || '').trim() || `Imported ${isNotesMode ? 'Notes' : 'Songs'} - ${formatUntitledDate()}`;
        const newSetData = baseSetShape(createdSetTitle, createdSetTunes, effectiveMode);
        if (navigator.onLine) {
          createdSetId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'sets', createdSetId), { id: createdSetId, ...newSetData });
        } else {
          createdSetId = crypto.randomUUID();
          await dexie.sets.put({ id: createdSetId, ...newSetData });
          await addPendingWrite({
            type: 'add',
            collection: 'sets',
            docId: createdSetId,
            data: { id: createdSetId, ...newSetData }
          });
        }
      }

      if (createMode === 'session' && importSessionId && createdSetTunes.length > 0) {
        const sessionData = {
          location: importSessionLoc,
          mode: effectiveMode,
          startedAt: importSessionDateStr,
          endedAt: importSessionDateStr,
          createdAt: importSessionDateStr,
          updatedAt: importSessionDateStr,
          lastViewedAt: importSessionDateStr,
          segments: [{ startedAt: importSessionDateStr, endedAt: importSessionDateStr }],
          notes: '',
          customTags: [],
          pinned: false,
        };
        if (navigator.onLine) {
          await setDoc(doc(db, 'users', user.uid, 'sessions', importSessionId), sessionData);
        } else {
          await dexie.sessions.put({ id: importSessionId, ...sessionData });
          await addPendingWrite({ type: 'add', collection: 'sessions', docId: importSessionId, data: { id: importSessionId, ...sessionData } });
        }
        createdSetId = importSessionId;
        createdSetTitle = importSessionLoc;
      }

      const collectionKind = createMode === 'session' ? 'session' : collectionLabel;
      onComplete?.(
        `Bulk import complete: ${importedCount} added${skippedCount ? `, ${skippedCount} skipped as duplicates` : ''}${enrichedCount ? `, ${enrichedCount} enriched from TheSession` : ''}${createdSetId ? `. New ${collectionKind} created: ${createdSetTitle}` : ''}.`
      );
      setBulkImportParsed([]);
      setBulkImportText('');
      setBulkImportTags([]);
      setCreateMode('none');
      setBulkImportSetTitle('');
      setBulkImportSessionLocation('');
      setBulkImportSessionDate('');
      setShowImportConfirm(false);
    } catch (err) {
      console.error('Bulk import failed:', err);
      alert('Bulk import failed.');
    } finally {
      setBulkImportLoading(false);
    }
  };

  return (
    <div className="bulk-import-card" style={{
      width: '100%',
      maxWidth: '300px',
      margin: '10px auto 0',
      padding: '8px',
      borderRadius: '10px',
      border: '1px solid color-mix(in srgb, var(--card-border) 72%, transparent)',
      background: 'color-mix(in srgb, var(--bg-card) 86%, var(--button-bg) 14%)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    }}>
      <div className="text-scaled" style={{ textAlign: 'center', fontWeight: 700 }}>
        Bulk {isNotesMode ? 'Note' : 'Song'} Import (beta)
      </div>
      <div className="text-scaled" style={{ textAlign: 'center', fontSize: 'var(--ui-subtle-font)', color: 'var(--text-muted)' }}>
        {isNotesMode
          ? noteParserMode === 'urtext'
            ? 'Paste Urtext text or upload .urtext/.txt. Parses brace nodes and skips TOC directives.'
            : 'Paste note text or upload .txt/.md. Uses separators, large gaps, and title_ lines.'
          : tuneParserMode === 'spreadsheet'
            ? 'Paste rows from Google Sheets/Excel, upload a .csv/.xlsx, or fetch a public Google Sheet URL.'
            : 'Paste chord/lyric text or upload .cho/.txt/.md'}
      </div>
      {isNotesMode && (
        <AppSelect
          className="search-core-select"
          value={noteParserMode}
          onChange={(value) => {
            const next = value || 'standard';
            setNoteParserMode(next);
            setBulkImportParsed(parseByMode(effectiveMode, bulkImportText, next));
            setShowImportConfirm(false);
          }}
          options={[
            { value: 'standard', label: 'Standard parser' },
            { value: 'urtext', label: 'Urtext (experimental)' },
          ]}
          placeholder="Parser mode"
          active={noteParserMode !== 'standard'}
        />
      )}
      {!isNotesMode && (
        <AppSelect
          className="search-core-select"
          value={tuneParserMode}
          onChange={(v) => { setTuneParserMode(v || 'standard'); setBulkImportParsed([]); setShowImportConfirm(false); }}
          options={[
            { value: 'standard', label: 'Standard (text / ChordPro)' },
            { value: 'spreadsheet', label: 'Spreadsheet (TSV / CSV / XLSX)' },
          ]}
          placeholder="Parser mode"
          active={tuneParserMode !== 'standard'}
        />
      )}
      {isNotesMode && (
        <div
          style={{
            border: '1px solid color-mix(in srgb, var(--card-border) 45%, transparent)',
            borderRadius: '8px',
            padding: '6px',
            background: 'color-mix(in srgb, var(--bg-card) 92%, transparent)',
          }}
        >
          <div className="text-scaled" style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Add tags to all imported notes
          </div>
          <CustomTagInput
            tags={bulkImportTags}
            setTags={setBulkImportTags}
            layout="inline"
            showInputByDefault={true}
            focusOnOpen={false}
            inputClassName="search-input"
            style={{ width: '100%' }}
            inputStyle={{ width: '100%' }}
          />
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px' }}>
        <AppSelect
          className="search-core-select"
          value={bulkImportGenre}
          onChange={setBulkImportGenre}
          options={bulkGenreSelectOptions}
          placeholder={isNotesMode ? 'Category' : 'Genre'}
          active={Boolean(bulkImportGenre)}
        />
        <AppSelect
          className="search-core-select"
          value={bulkImportType}
          onChange={setBulkImportType}
          options={bulkTypeSelectOptions}
          placeholder="Type"
          active={Boolean(bulkImportType)}
        />
      </div>
      {!isNotesMode && (
        <div>
          <label className="text-scaled" style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
            <input
              type="checkbox"
              checked={enrichFromTheSession}
              onChange={e => setEnrichFromTheSession(e.target.checked)}
            />
            Fetch ABC from TheSession (Irish tunes)
          </label>
          {enrichFromTheSession && (
            <div style={{ fontSize: 'var(--ui-subtle-font)', color: 'var(--text-muted)', textAlign: 'center', marginTop: '2px' }}>
              This will take significantly longer. Best match by title — not guaranteed to be correct. First 50 Irish tunes only.
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
        {['none', 'set', 'session'].map(opt => (
          <button
            key={opt}
            type="button"
            className="btn-subtle search-control-btn"
            style={{ fontWeight: createMode === opt ? 700 : 400, opacity: createMode === opt ? 1 : 0.6 }}
            onClick={() => setCreateMode(opt)}
          >
            {opt === 'none' ? 'No collection' : opt === 'set' ? `+ New ${isNotesMode ? 'Folder' : 'Set'}` : '+ New Session'}
          </button>
        ))}
      </div>
      {createMode === 'set' && (
        <input
          className="search-input"
          value={bulkImportSetTitle}
          onChange={(e) => setBulkImportSetTitle(e.target.value)}
          placeholder={`${collectionLabel.charAt(0).toUpperCase()}${collectionLabel.slice(1)} title (optional)`}
          style={{ width: '100%', boxSizing: 'border-box' }}
        />
      )}
      {createMode === 'session' && (
        <>
          <input
            className="search-input"
            value={bulkImportSessionLocation}
            onChange={(e) => setBulkImportSessionLocation(e.target.value)}
            placeholder="Location (e.g. The Cobblestone)"
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          <input
            className="search-input"
            type="date"
            value={bulkImportSessionDate}
            onChange={(e) => setBulkImportSessionDate(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={BULK_IMPORT_FILE_ACCEPT}
        multiple={isNotesMode && noteParserMode === 'urtext'}
        onChange={handleBulkImportFile}
        style={{ display: 'none' }}
      />
      {isNotesMode && noteParserMode === 'urtext' && (
        <input
          ref={(el) => {
            folderInputRef.current = el;
            if (el) el.setAttribute('webkitdirectory', '');
          }}
          type="file"
          onChange={handleBulkImportFile}
          style={{ display: 'none' }}
        />
      )}
      {isNotesMode && noteParserMode === 'urtext' ? (
        <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
          <button type="button" className="btn-subtle" onClick={() => fileInputRef.current?.click()} style={{ flex: 1 }}>
            Choose files
          </button>
          <button type="button" className="btn-subtle" onClick={() => folderInputRef.current?.click()} style={{ flex: 1 }}>
            Choose folder
          </button>
        </div>
      ) : (
        <button type="button" className="btn-subtle" onClick={() => fileInputRef.current?.click()} style={{ width: '100%' }}>
          Choose file
        </button>
      )}
      <textarea
        className="search-input add-entry-textarea"
        value={bulkImportText}
        onChange={(e) => setBulkImportText(e.target.value)}
        placeholder={
          isNotesMode
            ? noteParserMode === 'urtext'
              ? '<Thu., Oct. 26, 2023, 12:06 PM EDT>\n\nCantorial studies _\n\n{ Nusach modes _\n  { Adonai Malach _\n    kw::important\n    ...\n  }\n}'
              : 'Project Ideas_\nkw::[writing]-[personal]\n2/10/26\nDraft details...\n\n---\n\nMeeting Notes_\n...'
            : tuneParserMode === 'spreadsheet'
              ? 'Title\tType\tKey\tStatus\tGenre\nThe Morning Dew\tReel\tDmaj\tLearned\tIrish\nThe Silver Spear\tReel\tDmaj\t\tIrish'
              : 'Song Title\n[C]first line...\n\n---\n\nNext Song\n[G]...'
        }
        rows={6}
        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: '8rem' }}
      />
      {!isNotesMode && tuneParserMode === 'spreadsheet' && (
        <div style={{ display: 'flex', gap: '6px' }}>
          <input
            className="search-input"
            value={sheetUrl}
            onChange={e => setSheetUrl(e.target.value)}
            placeholder="Paste Google Sheet URL…"
            style={{ flex: 1 }}
          />
          <button
            className="btn-subtle search-control-btn"
            onClick={handleFetchSheet}
            disabled={sheetFetching}
            type="button"
          >
            {sheetFetching ? 'Fetching…' : 'Fetch'}
          </button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <button className="btn-subtle search-control-btn" type="button" onClick={previewBulkImport}>
          Preview
        </button>
        <button
          className="btn-subtle search-control-btn"
          type="button"
          onClick={requestImportConfirmation}
          disabled={bulkImportLoading}
        >
          {bulkImportLoading ? 'Importing…' : `Import ${isNotesMode ? 'Notes' : 'Songs'}${bulkImportParsed.length ? ` (${bulkImportParsed.length})` : ''}`}
        </button>
      </div>
      {showImportConfirm && (
        <div style={{
          border: '1px solid color-mix(in srgb, var(--card-border) 65%, transparent)',
          borderRadius: '8px',
          padding: '6px',
          background: 'color-mix(in srgb, var(--bg-card) 90%, var(--button-bg) 10%)'
        }}>
          <div className="text-scaled" style={{ textAlign: 'center', marginBottom: '4px' }}>
            Confirm import of {bulkImportParsed.length} titles
          </div>
          <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {bulkImportParsed.map((entry, idx) => (
              <div key={`${entry.title || 'entry'}-confirm-${idx}`}>{idx + 1}. {entry.title || '(untitled)'}</div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '6px' }}>
            <button
              className="btn-subtle search-control-btn"
              type="button"
              onClick={() => importBulkEntries(bulkImportParsed)}
              disabled={bulkImportLoading}
            >
              Confirm Import
            </button>
            <button className="btn-subtle search-control-btn" type="button" onClick={() => setShowImportConfirm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {bulkImportParsed.length > 0 && (
        <div style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {bulkImportParsed.slice(0, 8).map((entry, idx) => (
            <div key={`${entry.title || 'entry'}-${idx}`} style={{ padding: '4px 0', borderBottom: '1px solid color-mix(in srgb, var(--card-border) 40%, transparent)' }}>
              <div>
                {idx + 1}. {entry.title || '(untitled)'}
                {!isNotesMode && entry.type ? ` · ${entry.type}` : ''}
                {!isNotesMode && entry.key ? ` [${normalizeKey(entry.key)}]` : ''}
                {!isNotesMode && entry.status ? ` · ${entry.status}` : ''}
                {!isNotesMode && entry.quickNote ? ` · ${entry.quickNote}` : ''}
                {isNotesMode && entry.createdAtIso ? ` · ${new Date(entry.createdAtIso).toLocaleDateString()}` : ''}
              </div>
                {isNotesMode && bulkImportTags.length > 0 && (
                  <div style={{ opacity: 0.86 }}>global tags: {bulkImportTags.join(', ')}</div>
                )}
                {isNotesMode && Array.isArray(entry.tags) && entry.tags.length > 0 && (
                  <div style={{ opacity: 0.86 }}>tags: {entry.tags.join(', ')}</div>
                )}
              {isNotesMode && entry.notes && (
                <div style={{ opacity: 0.86, whiteSpace: 'pre-wrap' }}>
                  {String(entry.notes).split('\n').slice(0, 2).join('\n')}
                </div>
              )}
              {!isNotesMode && !!buildCombinedLyrics(entry) && (
                <div style={{ opacity: 0.86, whiteSpace: 'pre-wrap' }}>
                  {buildCombinedLyrics(entry).split('\n').slice(0, 2).join('\n')}
                </div>
              )}
            </div>
          ))}
          {bulkImportParsed.length > 8 && <div>...and {bulkImportParsed.length - 8} more</div>}
        </div>
      )}
    </div>
  );
}
